/* eslint-disable react/jsx-props-no-spreading */
import React, { FC, useCallback, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { Icons, Positions, Sizes, Stepper } from '@sede-enterprise/shell-ds-react-framework';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { Modal } from '../../NominationGroups/style';
import { Col, FormRow } from 'src/UI/Grid';
import Button from 'src/UI/Button';
import ErrorMessage from 'src/UI/ErrorMessage';
import FormControlSelect from 'src/UI/FormComponent/FormControlSelect';
import FormControlTextInput from 'src/UI/FormComponent/FormControlTextInput';
import FormControlDatePicker from 'src/UI/FormComponent/FormControlDatePicker';
import { get, post } from 'src/services/GenesisApi';
import { sendNotification } from 'src/UI/Notification';
import {
  GET_CONTRACT_TYPES,
  GET_MARKETS,
  GET_LOCATIONS,
  GET_NOMINATION_GROUPS,
  GET_ORGANIZATIONS,
  MAX_LENGTH_20,
  MAX_LENGTH_50,
  MESSAGES,
  ParameterScope,
  SET_CONTRACT,
} from 'src/constants/EUMarkets';
import {
  IAddEditContract,
  IContract,
  ILocationParameterDto,
  IMarketItem,
  INGParameters,
  IOrganization,
  IParameterDefinition,
} from 'src/types/EUMarkets.type';
import { ISelectOptions } from 'src/types/Common.type';
import { handleError } from 'src/utils/common/utils';
import {
  formatDateOnly,
  hasOverlappingParameterDates,
  isDateRangeValid,
  withResolvedDefinitionIds,
} from '../../Locations/AddEditParameter';
import InlineParameterList, {
  getParameterKey,
} from '../../Locations/AddEditParameter/InlineParameterList';

const { Add: AddIcon, ChevronDown, ChevronRight, Trash } = Icons;

// Flat contracts capture legal entities as free text instead of an organization lookup.
const FLAT_CONTRACT_TYPE_NAME = 'Flat';
const INTERNAL_ROLE_NAME = 'Internal';
const TRADING_PARTNER_ROLE_NAME = 'Trading Partner';

const hasRole = (organization: IOrganization, roleName: string) =>
  organization.roles?.some((role) => role.roleName === roleName);

const toOrganizationOption = (organization: IOrganization): ISelectOptions => ({
  id: organization.id,
  name: organization.organizationName,
});

interface IContractBalanceOption extends ISelectOptions {
  balanceName: string;
  organizationId: number;
}

const toContractBalanceOptions = (markets: IMarketItem[]): IContractBalanceOption[] =>
  markets.flatMap((market) =>
    (market.balances ?? []).map((balance) => ({
      id: balance.id,
      name: `${balance.balanceName} (${balance.organizationName})`,
      balanceName: balance.balanceName,
      organizationId: balance.organizationId,
    })),
  );

const toSavedContractBalanceOptions = (
  positions: IContract['positions'],
  knownBalanceIds: Set<string>,
  organizationId: number,
): IContractBalanceOption[] => {
  const savedBalanceOptions: IContractBalanceOption[] = [];

  positions.forEach((position) => {
    const balanceNames = String(position.balanceNames ?? '')
      .split(',')
      .map((balanceName) => balanceName.trim());

    (position.balanceIds ?? []).forEach((balanceId, index) => {
      if (knownBalanceIds.has(String(balanceId))) {
        return;
      }

      const balanceName = balanceNames[index] || `Balance ${balanceId}`;
      savedBalanceOptions.push({
        id: balanceId,
        name: balanceName,
        balanceName,
        organizationId,
      });
    });
  });

  return savedBalanceOptions;
};

// Resolves the legal entity name/id pair to submit: free text for Flat contracts, an organization lookup otherwise.
const resolveLegalEntityFields = (
  isFlatContractType: boolean,
  textValue: string,
  organizationId: string | number,
  organizationOptions: ISelectOptions[],
) => {
  if (isFlatContractType) {
    return { legalEntity: textValue, legalEntityId: null };
  }
  const organization = organizationOptions.find(
    (option) => String(option.id) === String(organizationId),
  );
  return {
    legalEntity: organization?.name ?? '',
    legalEntityId: organizationId ? Number(organizationId) : null,
  };
};

const directionOptions: ISelectOptions[] = [
  { id: 1, name: 'Buy' },
  { id: 2, name: 'Sell' },
  { id: 3, name: 'Neutral' },
];

const emptyPosition = {
  id: 0,
  positionName: '',
  aliasName: '',
  nominationGroupId: '',
  locationId: '',
  directionId: '',
  balanceIds: [],
  validFrom: dayjs(),
  validTo: null,
};

const defaultValues = {
  contractName: '',
  aliasName: '',
  contractTypeId: '',
  dealId: '',
  internalLegalEntity: '',
  externalLegalEntity: '',
  internalLegalEntityId: '',
  externalLegalEntityId: '',
  validFrom: dayjs(),
  validTo: null,
  positions: [emptyPosition],
};

const toOptions = (items: Record<string, unknown>[], nameKeys: string[]): ISelectOptions[] =>
  items.map((item) => {
    const nameKey = nameKeys.find((key) => item[key]);
    return {
      id: item.id as number,
      name: String(nameKey ? item[nameKey] : item.id),
    };
  });

const toContractTypeOptions = (items: Record<string, unknown>[]): ISelectOptions[] =>
  items.map((item) => {
    const id = (item.contractTypeId ?? item.id) as number;
    const nameKey = ['contractTypeName', 'name', 'description'].find((key) => item[key]);
    return {
      id,
      name: String(nameKey ? item[nameKey] : id),
    };
  });

const removePositionParameter = (
  positionParameters: INGParameters[][],
  positionIndex: number,
  parameterIndex: number,
): INGParameters[][] =>
  positionParameters.map((items, index) => {
    if (index !== positionIndex) {
      return items;
    }
    return items.filter((_, itemIndex) => itemIndex !== parameterIndex);
  });

// Drops the cancelled row and shifts down entries for later rows in the same position;
// entries for other positions or earlier rows in the same position are left untouched.
const shiftPositionParameterRecordOnCancel = <T,>(
  record: Record<string, T>,
  positionIndex: number,
  parameterIndex: number,
): Record<string, T> =>
  Object.entries(record).reduce<Record<string, T>>((next, [key, value]) => {
    const [entryPosition, entryIndex] = key.split('-').map(Number);
    if (entryPosition !== positionIndex || entryIndex < parameterIndex) {
      next[key] = value;
      return next;
    }
    if (entryIndex > parameterIndex) {
      next[`${positionIndex}-${entryIndex - 1}`] = value;
    }
    return next;
  }, {});

const toParameterDto = (parameter: INGParameters): ILocationParameterDto => ({
  parameterDefinitionId: parameter.parameterDefinitionId,
  valueString: parameter.valueString,
  valueLong: parameter.valueLong,
  valueDouble: parameter.valueDouble,
  valueBoolean: parameter.valueBoolean,
  valueListId: parameter.valueListId ?? null,
  valueListValue: parameter.valueListValue ?? null,
  validFrom: parameter.validFrom,
  validTo: parameter.validTo,
});

const areParameterDatesWithinParentRange = (
  parameter: INGParameters,
  parentValidFrom: Dayjs | undefined,
  parentValidTo: Dayjs | null,
) => {
  if (!parentValidFrom?.isValid()) {
    return true;
  }

  const parameterValidFrom = dayjs(parameter.validFrom);
  if (!parameterValidFrom.isValid() || parameterValidFrom.isBefore(parentValidFrom, 'day')) {
    return false;
  }

  if (!parentValidTo?.isValid()) {
    return true;
  }

  const parameterValidTo = parameter.validTo ? dayjs(parameter.validTo) : null;
  return Boolean(parameterValidTo?.isValid() && !parameterValidTo.isAfter(parentValidTo, 'day'));
};

const areDatesWithinParentRange = (
  childValidFrom: Dayjs | undefined,
  childValidTo: Dayjs | null,
  parentValidFrom: Dayjs | undefined,
  parentValidTo: Dayjs | null,
) => {
  if (!parentValidFrom?.isValid()) {
    return true;
  }

  if (!childValidFrom?.isValid() || childValidFrom.isBefore(parentValidFrom, 'day')) {
    return false;
  }

  if (!parentValidTo?.isValid()) {
    return true;
  }

  return Boolean(childValidTo?.isValid() && !childValidTo.isAfter(parentValidTo, 'day'));
};

const buildMergedContractParametersRaw = (
  parametersList: INGParameters[],
  drafts: Record<number, INGParameters>,
  pendingCount: number,
): INGParameters[] => [
  ...parametersList.map((parameter, index) => drafts[index] ?? parameter),
  ...Array.from(
    { length: pendingCount },
    (_, pendingIndex) => drafts[parametersList.length + pendingIndex],
  ).filter((parameter): parameter is INGParameters => Boolean(parameter)),
];

// Recomputes the contract parameters banner message from scratch so it can be re-checked after edits or removals.
const getContractParametersError = (
  parametersList: INGParameters[],
  drafts: Record<number, INGParameters>,
  draftErrors: Record<number, string>,
  pendingCount: number,
  parentValidFrom: Dayjs | undefined,
  parentValidTo: Dayjs | null,
): string => {
  const firstDraftError = Object.values(draftErrors).find(Boolean);
  if (firstDraftError) {
    return firstDraftError;
  }
  const pendingParameterIndexes = Array.from(
    { length: pendingCount },
    (_, pendingIndex) => parametersList.length + pendingIndex,
  );
  if (pendingParameterIndexes.some((index) => !drafts[index]?.parameterDefinitionId)) {
    return MESSAGES.REQUIRED_PARAMETER_DEFINITION;
  }
  const mergedParametersRaw = buildMergedContractParametersRaw(
    parametersList,
    drafts,
    pendingCount,
  );
  if (
    mergedParametersRaw.some(
      (parameter) => !areParameterDatesWithinParentRange(parameter, parentValidFrom, parentValidTo),
    )
  ) {
    return MESSAGES.PARAMETER_DATE_OUTSIDE_CONTRACT_RANGE;
  }
  if (hasOverlappingParameterDates(mergedParametersRaw)) {
    return MESSAGES.OVERLAPPING_PARAMETER_DATE_RANGE;
  }
  return '';
};

const AddEditContract: FC<IAddEditContract> = ({
  open = false,
  onClose = () => {},
  contract = null,
  refreshData = () => {},
}) => {
  const methods = useForm({ mode: 'onChange' });
  const { reset, watch, setValue, setError, clearErrors, trigger, control } = methods;
  const {
    fields: positionFields,
    append: appendPosition,
    remove: removePosition,
  } = useFieldArray({
    control,
    name: 'positions',
  });
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingLookups, setLoadingLookups] = useState<boolean>(false);
  const [contractTypes, setContractTypes] = useState<ISelectOptions[]>([]);
  const [nominationGroups, setNominationGroups] = useState<ISelectOptions[]>([]);
  const [locations, setLocations] = useState<ISelectOptions[]>([]);
  const [balanceOptions, setBalanceOptions] = useState<IContractBalanceOption[]>([]);
  const [internalOrganizations, setInternalOrganizations] = useState<ISelectOptions[]>([]);
  const [externalOrganizations, setExternalOrganizations] = useState<ISelectOptions[]>([]);
  const [parameters, setParameters] = useState<INGParameters[]>([]);
  const [parameterDrafts, setParameterDrafts] = useState<Record<number, INGParameters>>({});
  const [parameterDraftErrors, setParameterDraftErrors] = useState<Record<number, string>>({});
  const [addingContractParameterCount, setAddingContractParameterCount] = useState(0);
  const [positionParameters, setPositionParameters] = useState<INGParameters[][]>([]);
  const [positionParameterDrafts, setPositionParameterDrafts] = useState<
    Record<string, INGParameters>
  >({});
  const [positionParameterErrors, setPositionParameterErrors] = useState<Record<string, string>>(
    {},
  );
  const [addingPositionParameters, setAddingPositionParameters] = useState<Record<number, number>>(
    {},
  );
  const [expandedPositions, setExpandedPositions] = useState<Record<string, boolean>>({});
  const [positionsError, setPositionsError] = useState('');
  const validFrom = watch?.('validFrom');
  const validTo = watch?.('validTo');
  const watchedPositions = watch?.('positions') || [];
  const contractTypeId = watch?.('contractTypeId');
  const isFlatContractType =
    contractTypes.find((type) => String(type.id) === String(contractTypeId))?.name ===
    FLAT_CONTRACT_TYPE_NAME;
  const parametersErrorMessage = methods.formState?.errors?.parameters?.message as
    string | undefined;

  React.useEffect(() => {
    if (contract) {
      reset({
        contractName: contract.contractName,
        aliasName: contract.aliasName,
        contractTypeId: contract.contractTypeId ?? '',
        dealId: contract.dealId ?? '',
        internalLegalEntity: contract.internalLegalEntity,
        externalLegalEntity: contract.externalLegalEntity,
        internalLegalEntityId: contract.internalLegalEntityId ?? '',
        externalLegalEntityId: contract.externalLegalEntityId ?? '',
        validFrom: contract.validFrom ? dayjs(contract.validFrom) : undefined,
        validTo: contract.validTo ? dayjs(contract.validTo) : null,
        positions: (contract.positions || []).map((position) => ({
          ...position,
          balanceIds: position.balanceIds ?? [],
          validFrom: position.validFrom ? dayjs(position.validFrom) : dayjs(),
          validTo: position.validTo ? dayjs(position.validTo) : null,
        })),
      });
      setParameters(contract.parameters);
      setPositionParameters(
        (contract.positions || []).map((position) => position.parameters || []),
      );
      setExpandedPositions(
        Object.fromEntries(
          (contract.positions || []).map((position) => [String(position.id), false]),
        ),
      );
      setParameterDrafts({});
      setAddingPositionParameters({});
      setCurrentStep(0);
      return;
    }
    reset(defaultValues);
    setParameters([]);
    setParameterDrafts({});
    setAddingContractParameterCount(0);
    setPositionParameters([[]]);
    setPositionParameterDrafts({});
    setCurrentStep(0);
  }, [contract, open, reset]);

  React.useEffect(() => {
    if (!open) {
      return;
    }
    setLoadingLookups(true);
    Promise.all([
      get(GET_CONTRACT_TYPES),
      get(GET_NOMINATION_GROUPS),
      get(GET_LOCATIONS),
      get(GET_ORGANIZATIONS),
      get(GET_MARKETS),
    ])
      .then(
        ([
          contractTypeResponse,
          nominationGroupResponse,
          locationResponse,
          organizationResponse,
          marketResponse,
        ]) => {
          setContractTypes(toContractTypeOptions(contractTypeResponse.data || []));
          setNominationGroups(
            toOptions(nominationGroupResponse.data || [], ['nominationGroupName', 'name']),
          );
          setLocations(toOptions(locationResponse.data || [], ['locationName', 'name']));
          const allOrganizations = (organizationResponse.data || []) as IOrganization[];
          setInternalOrganizations(
            allOrganizations
              .filter((organization) => hasRole(organization, INTERNAL_ROLE_NAME))
              .map(toOrganizationOption),
          );
          setExternalOrganizations(
            allOrganizations
              .filter((organization) => hasRole(organization, TRADING_PARTNER_ROLE_NAME))
              .map(toOrganizationOption),
          );
          const nextBalanceOptions = toContractBalanceOptions(marketResponse.data || []);
          const knownBalanceIds = new Set(nextBalanceOptions.map((option) => String(option.id)));
          const savedBalanceOptions = toSavedContractBalanceOptions(
            contract?.positions ?? [],
            knownBalanceIds,
            contract?.internalLegalEntityId ?? 0,
          );
          setBalanceOptions([...nextBalanceOptions, ...savedBalanceOptions]);
          (contract?.positions || []).forEach((position, positionIndex) => {
            setValue(`positions.${positionIndex}.balanceIds`, position.balanceIds ?? []);
          });
        },
      )
      .catch((error) => handleError(error, 'Unable to load contract form options.'))
      .finally(() => setLoadingLookups(false));
  }, [open]);

  const handleClose = () => {
    reset(defaultValues);
    setParameters([]);
    setParameterDrafts({});
    setParameterDraftErrors({});
    setAddingContractParameterCount(0);
    setPositionParameters([]);
    setPositionParameterDrafts({});
    setPositionParameterErrors({});
    setAddingPositionParameters({});
    setExpandedPositions({});
    setPositionsError('');
    setCurrentStep(0);
    clearErrors('parameters');
    onClose();
  };

  const handleValidFromChange = (newValidFrom: Dayjs | null) => {
    if (newValidFrom && validTo && dayjs(newValidFrom).isAfter(dayjs(validTo), 'day')) {
      setValue('validTo', null, { shouldValidate: true });
    }
  };

  // Refreshes the banner only once one is showing, so newly added rows don't error before a save.
  const syncParametersError = useCallback(
    (nextErrors: Record<number, string>) => {
      const firstError = Object.values(nextErrors).find(Boolean);
      if (!firstError) {
        clearErrors('parameters');
        return;
      }
      if (parametersErrorMessage) {
        setError('parameters', { type: 'validate', message: firstError });
      }
    },
    [clearErrors, setError, parametersErrorMessage],
  );

  const handleParameterDraftChange = useCallback(
    (parameter: INGParameters, rowIndex: number, error?: string) => {
      setParameterDrafts((currentDrafts) => ({ ...currentDrafts, [rowIndex]: parameter }));
      setParameterDraftErrors((currentErrors) => {
        const nextErrors = { ...currentErrors, [rowIndex]: error ?? '' };
        syncParametersError(nextErrors);
        return nextErrors;
      });
    },
    [syncParametersError],
  );

  const handleParameterRemove = useCallback(
    (parameterIndex: number) => {
      const newParameters = parameters.filter((_, index) => index !== parameterIndex);
      setParameters(newParameters);
      if (parametersErrorMessage) {
        const error = getContractParametersError(
          newParameters,
          parameterDrafts,
          parameterDraftErrors,
          addingContractParameterCount,
          validFrom,
          validTo,
        );
        if (!error) {
          clearErrors('parameters');
        } else {
          setError('parameters', { type: 'validate', message: error });
        }
      }
    },
    [
      parameters,
      parameterDrafts,
      parameterDraftErrors,
      addingContractParameterCount,
      validFrom,
      validTo,
      parametersErrorMessage,
      clearErrors,
      setError,
    ],
  );

  const handleCancelParameterAdd = useCallback(
    (parameterIndex?: number) => {
      if (parameterIndex === undefined) {
        return;
      }
      const nextDrafts = Object.entries(parameterDrafts).reduce<Record<number, INGParameters>>(
        (drafts, [index, draft]) => {
          const currentIndex = Number(index);
          if (currentIndex !== parameterIndex) {
            drafts[currentIndex > parameterIndex ? currentIndex - 1 : currentIndex] = draft;
          }
          return drafts;
        },
        {},
      );
      const nextErrors = Object.entries(parameterDraftErrors).reduce<Record<number, string>>(
        (errors, [index, error]) => {
          const currentIndex = Number(index);
          if (currentIndex !== parameterIndex) {
            errors[currentIndex > parameterIndex ? currentIndex - 1 : currentIndex] = error;
          }
          return errors;
        },
        {},
      );
      const nextPendingCount = Math.max(0, addingContractParameterCount - 1);
      setParameterDrafts(nextDrafts);
      setParameterDraftErrors(nextErrors);
      setAddingContractParameterCount(nextPendingCount);
      if (parametersErrorMessage) {
        const error = getContractParametersError(
          parameters,
          nextDrafts,
          nextErrors,
          nextPendingCount,
          validFrom,
          validTo,
        );
        if (!error) {
          clearErrors('parameters');
        } else {
          setError('parameters', { type: 'validate', message: error });
        }
      }
    },
    [
      parameters,
      parameterDrafts,
      parameterDraftErrors,
      addingContractParameterCount,
      validFrom,
      validTo,
      parametersErrorMessage,
      clearErrors,
      setError,
    ],
  );

  const getMergedContractParameters = () => {
    const mergedParametersRaw = buildMergedContractParametersRaw(
      parameters,
      parameterDrafts,
      addingContractParameterCount,
    );
    return {
      raw: mergedParametersRaw,
      unique: mergedParametersRaw.filter(
        (parameter, index, allParameters) =>
          allParameters.findIndex(
            (candidate) => getParameterKey(candidate) === getParameterKey(parameter),
          ) === index,
      ),
    };
  };

  const validateContractParameters = () => {
    const error = getContractParametersError(
      parameters,
      parameterDrafts,
      parameterDraftErrors,
      addingContractParameterCount,
      validFrom,
      validTo,
    );
    if (error) {
      setError('parameters', { type: 'validate', message: error });
      return false;
    }
    clearErrors('parameters');
    return true;
  };

  // Re-checks the parameters banner after a row is removed/cancelled so a resolved error clears automatically.
  React.useEffect(() => {
    if (parametersErrorMessage) {
      validateContractParameters();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    parameters,
    parameterDrafts,
    parameterDraftErrors,
    addingContractParameterCount,
    validFrom,
    validTo,
  ]);

  const handleNext = async () => {
    const legalEntityFieldNames = isFlatContractType
      ? ['internalLegalEntity', 'externalLegalEntity']
      : ['internalLegalEntityId', 'externalLegalEntityId'];
    const contractIsValid = await trigger([
      'contractName',
      'aliasName',
      'contractTypeId',
      ...legalEntityFieldNames,
      'validFrom',
      'validTo',
    ]);
    if (contractIsValid && validateContractParameters()) {
      setCurrentStep(1);
    }
  };

  const handleAddPosition = () => {
    appendPosition(emptyPosition);
    const newIndex = positionFields.length;
    setPositionParameters((current) => [...current, []]);
    setExpandedPositions((current) => ({ ...current, [`new-${newIndex}`]: true }));
    setPositionsError('');
  };

  const handleRemovePosition = (positionIndex: number) => {
    removePosition(positionIndex);
    setPositionParameters((current) => current.filter((_, index) => index !== positionIndex));
    setPositionParameterDrafts({});
    setPositionParameterErrors({});
    setAddingPositionParameters({});
  };

  const handlePositionParameterDraftChange = useCallback(
    (positionIndex: number, parameter: INGParameters, parameterIndex: number, error?: string) => {
      setPositionParameterDrafts((current) => ({
        ...current,
        [`${positionIndex}-${parameterIndex}`]: parameter,
      }));
      setPositionParameterErrors((current) => ({
        ...current,
        [`${positionIndex}-${parameterIndex}`]: error || '',
      }));
    },
    [],
  );

  const handlePositionDefinitionsLoaded = (
    positionIndex: number,
    definitions: IParameterDefinition[],
  ) => {
    setPositionParameters((current) => {
      const updated = [...current];
      updated[positionIndex] = withResolvedDefinitionIds(updated[positionIndex] || [], definitions);
      return updated;
    });
  };

  const handlePositionParameterRemove = (positionIndex: number, parameterIndex: number) => {
    setPositionParameters((current) =>
      removePositionParameter(current, positionIndex, parameterIndex),
    );
    setPositionParameterDrafts({});
    setPositionParameterErrors({});
  };

  const getMergedPositionParameters = (positionIndex: number) => {
    const currentParameters = positionParameters[positionIndex] || [];
    const pendingCount = addingPositionParameters[positionIndex] || 0;
    const merged = [
      ...currentParameters.map(
        (parameter, parameterIndex) =>
          positionParameterDrafts[`${positionIndex}-${parameterIndex}`] || parameter,
      ),
      ...Array.from(
        { length: pendingCount },
        (_, pendingIndex) =>
          positionParameterDrafts[`${positionIndex}-${currentParameters.length + pendingIndex}`],
      ).filter((parameter): parameter is INGParameters => Boolean(parameter)),
    ];
    return merged.filter(
      (parameter, index, allParameters) =>
        allParameters.findIndex(
          (candidate) => getParameterKey(candidate) === getParameterKey(parameter),
        ) === index,
    );
  };

  // Recomputes the positions banner message from scratch so it can be re-checked live and on submit.
  const getPositionsValidationError = (): string => {
    if (!watchedPositions?.length) {
      return MESSAGES.REQUIRED_POSITION;
    }
    const hasPositionOutsideContractRange = watchedPositions.some(
      (position) =>
        !areDatesWithinParentRange(
          position?.validFrom,
          position?.validTo ?? null,
          validFrom,
          validTo,
        ),
    );
    if (hasPositionOutsideContractRange) {
      return MESSAGES.POSITION_DATE_OUTSIDE_CONTRACT_RANGE;
    }
    const hasIncompletePositionParameter = positionParameters.some(
      (currentPositionParameters, positionIndex) => {
        const pendingCount = addingPositionParameters[positionIndex] || 0;
        return Array.from(
          { length: pendingCount },
          (_, pendingIndex) => currentPositionParameters.length + pendingIndex,
        ).some(
          (parameterIndex) =>
            !positionParameterDrafts[`${positionIndex}-${parameterIndex}`]?.parameterDefinitionId,
        );
      },
    );
    if (hasIncompletePositionParameter) {
      return MESSAGES.REQUIRED_PARAMETER_DEFINITION;
    }
    const firstPositionParameterDraftError = Object.values(positionParameterErrors).find(Boolean);
    if (firstPositionParameterDraftError) {
      return firstPositionParameterDraftError;
    }
    const hasPositionParameterWithoutDefinition = positionParameters.some((_, positionIndex) =>
      getMergedPositionParameters(positionIndex).some(
        (parameter) =>
          !Number.isSafeInteger(Number(parameter.parameterDefinitionId)) ||
          Number(parameter.parameterDefinitionId) <= 0,
      ),
    );
    if (hasPositionParameterWithoutDefinition) {
      return MESSAGES.UNRESOLVED_PARAMETER_DEFINITION;
    }
    const hasPositionParameterOutsideRange = positionParameters.some((_, positionIndex) => {
      const position = watchedPositions[positionIndex];
      return getMergedPositionParameters(positionIndex).some(
        (parameter) =>
          !areParameterDatesWithinParentRange(
            parameter,
            position?.validFrom,
            position?.validTo ?? null,
          ),
      );
    });
    if (hasPositionParameterOutsideRange) {
      return MESSAGES.PARAMETER_DATE_OUTSIDE_POSITION_RANGE;
    }
    const hasPositionParameterOverlap = positionParameters.some((_, positionIndex) =>
      hasOverlappingParameterDates(getMergedPositionParameters(positionIndex)),
    );
    if (hasPositionParameterOverlap) {
      return MESSAGES.OVERLAPPING_PARAMETER_DATE_RANGE;
    }
    return '';
  };

  // Refreshes the positions banner only once one is showing, so newly added rows don't error before a save.
  React.useEffect(() => {
    if (positionsError) {
      setPositionsError(getPositionsValidationError());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    positionParameters,
    positionParameterDrafts,
    positionParameterErrors,
    addingPositionParameters,
    watchedPositions,
    validFrom,
    validTo,
  ]);

  const handleSubmit = (formData) => {
    const positionsValidationError = getPositionsValidationError();
    if (positionsValidationError || !validateContractParameters()) {
      setPositionsError(positionsValidationError);
      return;
    }
    const mergedParameters = getMergedContractParameters().unique;
    const internalLegalEntity = resolveLegalEntityFields(
      isFlatContractType,
      formData.internalLegalEntity,
      formData.internalLegalEntityId,
      internalOrganizations,
    );
    const externalLegalEntity = resolveLegalEntityFields(
      isFlatContractType,
      formData.externalLegalEntity,
      formData.externalLegalEntityId,
      externalOrganizations,
    );
    setPositionsError('');
    setLoading(true);
    const isUpdate = Boolean(contract);
    post(SET_CONTRACT, {
      id: contract?.id ?? 0,
      contractName: formData.contractName,
      aliasName: formData.aliasName,
      contractTypeId: Number(formData.contractTypeId),
      dealId: String(formData.dealId ?? '').trim() || null,
      internalLegalEntity: internalLegalEntity.legalEntity,
      externalLegalEntity: externalLegalEntity.legalEntity,
      internalLegalEntityId: internalLegalEntity.legalEntityId,
      externalLegalEntityId: externalLegalEntity.legalEntityId,
      isActive: contract?.isActive ?? true,
      validFrom: formatDateOnly(formData.validFrom as Dayjs),
      validTo: formData.validTo ? formatDateOnly(formData.validTo as Dayjs) : null,
      parameters: mergedParameters.map(toParameterDto),
      positions: formData.positions.map((position, positionIndex) => ({
        id: position.id ?? 0,
        positionName: position.positionName,
        aliasName: position.aliasName,
        nominationGroupId: Number(position.nominationGroupId),
        locationId: Number(position.locationId),
        directionId: Number(position.directionId),
        balanceIds: Array.isArray(position.balanceIds) ? position.balanceIds.map(Number) : [],
        validFrom: formatDateOnly(position.validFrom as Dayjs),
        validTo: position.validTo ? formatDateOnly(position.validTo as Dayjs) : null,
        parameters: getMergedPositionParameters(positionIndex).map(toParameterDto),
      })),
    })
      .then(() => {
        sendNotification({
          type: 'success',
          message: isUpdate ? MESSAGES.UPDATED_CONTRACT : MESSAGES.CREATED_CONTRACT,
        });
        refreshData();
        handleClose();
      })
      .catch((error) => {
        handleError(
          error,
          isUpdate ? MESSAGES.UPDATED_CONTRACT_FAILED : MESSAGES.CREATED_CONTRACT_FAILED,
        );
      })
      .finally(() => setLoading(false));
  };

  return (
    <Modal
      className='parameter-edit-modal contract-edit-modal'
      title={`${contract ? 'Modify' : 'Add a new'} contract`}
      open={open}
      onClose={handleClose}
      maskClosable={false}
      isDraggable
      width={1200}
      footer={
        <div className='button-style'>
          <Button type='button' onClick={handleClose} variant='outlined' data-testid='btn-cancel'>
            Cancel
          </Button>
          {currentStep === 0 ? (
            <Button data-testid='next-step' type='button' onClick={handleNext}>
              Next
            </Button>
          ) : (
            <>
              <Button type='button' variant='outlined' onClick={() => setCurrentStep(0)}>
                Back
              </Button>
              <Button
                data-testid='submit-form'
                type='button'
                loading={loading}
                disabled={loading}
                onClick={methods.handleSubmit(handleSubmit)}
              >
                Save
              </Button>
            </>
          )}
        </div>
      }
    >
      <FormProvider {...methods}>
        <Stepper
          className='contract-stepper'
          current={currentStep}
          size={Sizes.Small}
          steps={[
            { title: 'Contract', order: 1 },
            { title: 'Positions', order: 2 },
          ]}
        />
        <div hidden={currentStep !== 0}>
          <FormRow className='modal-form-row'>
            <Col size={4}>
              <FormControlTextInput
                controllerProps={{
                  name: 'contractName',
                  rules: {
                    required: MESSAGES.REQUIRED_CONTRACT_NAME,
                    maxLength: { value: MAX_LENGTH_50, message: MESSAGES.MAX_LENGTH_50 },
                  },
                }}
                label='Contract Name'
                methods={methods}
                inputProps={{ placeholder: 'Enter Contract Name' }}
              />
            </Col>
            <Col size={4}>
              <FormControlTextInput
                controllerProps={{
                  name: 'aliasName',
                  rules: {
                    maxLength: { value: MAX_LENGTH_20, message: MESSAGES.MAX_LENGTH_20 },
                  },
                }}
                label='Alias Name'
                methods={methods}
                inputProps={{ placeholder: 'Enter Alias Name' }}
              />
            </Col>
            <Col size={4}>
              <FormControlSelect
                controllerProps={{
                  name: 'contractTypeId',
                  rules: { required: MESSAGES.REQUIRED_CONTRACT_TYPE },
                }}
                label='Contract Type'
                methods={methods}
                isLoading={loadingLookups}
                data={contractTypes}
              />
            </Col>
          </FormRow>
          <FormRow className='modal-form-row'>
            <Col size={4}>
              <FormControlTextInput
                methods={methods}
                label='Deal Id'
                controllerProps={{
                  name: 'dealId',
                  rules: {
                    maxLength: {
                      value: MAX_LENGTH_50,
                      message: MESSAGES.MAX_LENGTH_50,
                    },
                  },
                }}
                inputProps={{ placeholder: 'Enter Deal Id', maxLength: MAX_LENGTH_50 }}
              />
            </Col>
            <Col size={4}>
              {isFlatContractType ? (
                <FormControlTextInput
                  controllerProps={{
                    name: 'internalLegalEntity',
                    rules: {
                      required: MESSAGES.REQUIRED_INTERNAL_LEGAL_ENTITY,
                      maxLength: { value: MAX_LENGTH_50, message: MESSAGES.MAX_LENGTH_50 },
                    },
                  }}
                  label='Internal Legal Entity'
                  methods={methods}
                  inputProps={{ placeholder: 'Enter Internal Legal Entity' }}
                />
              ) : (
                <FormControlSelect
                  controllerProps={{
                    name: 'internalLegalEntityId',
                    rules: { required: MESSAGES.REQUIRED_INTERNAL_ORGANIZATION },
                  }}
                  label='Internal Legal Entity'
                  methods={methods}
                  isLoading={loadingLookups}
                  data={internalOrganizations}
                />
              )}
            </Col>
            <Col size={4}>
              {isFlatContractType ? (
                <FormControlTextInput
                  controllerProps={{
                    name: 'externalLegalEntity',
                    rules: {
                      required: MESSAGES.REQUIRED_EXTERNAL_LEGAL_ENTITY,
                      maxLength: { value: MAX_LENGTH_50, message: MESSAGES.MAX_LENGTH_50 },
                    },
                  }}
                  label='External Legal Entity'
                  methods={methods}
                  inputProps={{ placeholder: 'Enter External Legal Entity' }}
                />
              ) : (
                <FormControlSelect
                  controllerProps={{
                    name: 'externalLegalEntityId',
                    rules: { required: MESSAGES.REQUIRED_EXTERNAL_ORGANIZATION },
                  }}
                  label='External Legal Entity'
                  methods={methods}
                  isLoading={loadingLookups}
                  data={externalOrganizations}
                />
              )}
            </Col>
          </FormRow>
          <FormRow className='modal-form-row'>
            <Col size={6}>
              <FormControlDatePicker
                methods={methods}
                label='Valid From'
                controllerProps={{
                  name: 'validFrom',
                  rules: {
                    required: MESSAGES.REQUIRED_VALID_FROM,
                    validate: (value) =>
                      isDateRangeValid(value, validTo) || MESSAGES.INVALID_PARAMETER_DATE_RANGE,
                  },
                }}
                handleOnChange={handleValidFromChange}
              />
            </Col>
            <Col size={6}>
              <FormControlDatePicker
                methods={methods}
                label='Valid To'
                controllerProps={{
                  name: 'validTo',
                  rules: {
                    validate: (value) =>
                      isDateRangeValid(validFrom, value) || MESSAGES.INVALID_PARAMETER_DATE_RANGE,
                  },
                }}
                inputProps={{
                  disabledDate: (current: Dayjs) =>
                    validFrom ? current.isBefore(validFrom, 'day') : false,
                }}
              />
            </Col>
          </FormRow>
          <div className='group-details group-details-with-action'>
            <strong className='param-title'>Contract Parameters</strong>
            <Button
              onClick={() => setAddingContractParameterCount((currentCount) => currentCount + 1)}
              icon={<AddIcon />}
              size={Sizes.Small}
              variant='outlined'
              iconPosition={Positions.Right}
              data-testid='add-contract-parameter'
            >
              Add Row
            </Button>
          </div>
          <div className='parameter-table'>
            <InlineParameterList
              open={open}
              scopeId={ParameterScope.Contract}
              locationId={contract?.id ?? 0}
              parameters={parameters}
              isAdding={addingContractParameterCount > 0}
              addingParameterCount={addingContractParameterCount}
              onCancelAdd={handleCancelParameterAdd}
              onRemove={handleParameterRemove}
              onDraftChange={handleParameterDraftChange}
            />
            {parametersErrorMessage && <ErrorMessage>{parametersErrorMessage}</ErrorMessage>}
          </div>
        </div>
        <div className='positions-step' hidden={currentStep !== 1}>
          <div className='group-details group-details-with-action'>
            <strong className='param-title'>Positions</strong>
            <Button
              onClick={handleAddPosition}
              icon={<AddIcon />}
              size={Sizes.Small}
              variant='filled'
              iconPosition={Positions.Right}
              data-testid='add-position'
            >
              Add Position
            </Button>
          </div>
          {positionFields.map((field, positionIndex) => {
            const expansionKey = field.id || `new-${positionIndex}`;
            const isExpanded = expandedPositions[expansionKey] !== false;
            const positionValidFrom = watchedPositions[positionIndex]?.validFrom;
            const positionValidTo = watchedPositions[positionIndex]?.validTo;
            return (
              <div className='parameter-table' key={field.id}>
                <div className='group-details group-details-with-action'>
                  <Button
                    type='button'
                    variant='transparent'
                    icon={isExpanded ? <ChevronDown /> : <ChevronRight />}
                    onClick={() =>
                      setExpandedPositions((current) => ({
                        ...current,
                        [expansionKey]: !isExpanded,
                      }))
                    }
                    data-testid={`toggle-position-${positionIndex}`}
                  >
                    {watchedPositions[positionIndex]?.positionName ||
                      `Position ${positionIndex + 1}`}
                  </Button>
                  <Button
                    type='button'
                    variant='transparent'
                    icon={<Trash />}
                    iconOnly
                    onClick={() => handleRemovePosition(positionIndex)}
                    data-testid={`remove-position-${positionIndex}`}
                  />
                </div>
                {isExpanded && (
                  <>
                    <FormRow>
                      <Col size={4}>
                        <FormControlTextInput
                          methods={methods}
                          label='Position Name'
                          controllerProps={{
                            name: `positions.${positionIndex}.positionName`,
                            rules: {
                              required: MESSAGES.REQUIRED_POSITION_NAME,
                              maxLength: {
                                value: MAX_LENGTH_50,
                                message: MESSAGES.MAX_LENGTH_50,
                              },
                            },
                          }}
                          inputProps={{ placeholder: 'Enter Position Name' }}
                        />
                      </Col>
                      <Col size={4}>
                        <FormControlTextInput
                          methods={methods}
                          label='Alias Name'
                          controllerProps={{
                            name: `positions.${positionIndex}.aliasName`,
                            rules: {
                              maxLength: {
                                value: MAX_LENGTH_20,
                                message: MESSAGES.MAX_LENGTH_20,
                              },
                            },
                          }}
                          inputProps={{ placeholder: 'Enter Alias Name' }}
                        />
                      </Col>
                      <Col size={4}>
                        <FormControlSelect
                          methods={methods}
                          label='Nomination Group'
                          isLoading={loadingLookups}
                          data={nominationGroups}
                          controllerProps={{
                            name: `positions.${positionIndex}.nominationGroupId`,
                            rules: { required: MESSAGES.REQUIRED_NOMINATION_GROUP },
                          }}
                        />
                      </Col>
                    </FormRow>
                    <FormRow>
                      <Col size={4}>
                        <FormControlSelect
                          methods={methods}
                          label='Location'
                          isLoading={loadingLookups}
                          data={locations}
                          controllerProps={{
                            name: `positions.${positionIndex}.locationId`,
                            rules: { required: MESSAGES.REQUIRED_LOCATION_NAME },
                          }}
                        />
                      </Col>
                      <Col size={4}>
                        <FormControlSelect
                          methods={methods}
                          label='Direction'
                          isLoading={false}
                          data={directionOptions}
                          controllerProps={{
                            name: `positions.${positionIndex}.directionId`,
                            rules: { required: MESSAGES.REQUIRED_DIRECTION },
                          }}
                        />
                      </Col>
                    </FormRow>
                    <FormRow>
                      <Col size={12}>
                        <FormControlSelect
                          methods={methods}
                          label='Balances'
                          isLoading={loadingLookups}
                          data={balanceOptions}
                          selectProps={{ mode: 'multiple', placeholder: 'Select balances' }}
                          controllerProps={{ name: `positions.${positionIndex}.balanceIds` }}
                        />
                      </Col>
                    </FormRow>
                    <FormRow>
                      <Col size={6}>
                        <FormControlDatePicker
                          methods={methods}
                          label='Valid From'
                          controllerProps={{
                            name: `positions.${positionIndex}.validFrom`,
                            rules: {
                              required: MESSAGES.REQUIRED_VALID_FROM,
                              validate: (value) =>
                                isDateRangeValid(value, positionValidTo) ||
                                MESSAGES.INVALID_PARAMETER_DATE_RANGE,
                            },
                          }}
                          handleOnChange={(newValidFrom) => {
                            if (
                              newValidFrom &&
                              positionValidTo &&
                              dayjs(newValidFrom).isAfter(dayjs(positionValidTo), 'day')
                            ) {
                              setValue(`positions.${positionIndex}.validTo`, null, {
                                shouldValidate: true,
                              });
                            }
                          }}
                        />
                      </Col>
                      <Col size={6}>
                        <FormControlDatePicker
                          methods={methods}
                          label='Valid To'
                          controllerProps={{
                            name: `positions.${positionIndex}.validTo`,
                            rules: {
                              validate: (value) =>
                                isDateRangeValid(positionValidFrom, value) ||
                                MESSAGES.INVALID_PARAMETER_DATE_RANGE,
                            },
                          }}
                          inputProps={{
                            disabledDate: (current: Dayjs) =>
                              positionValidFrom
                                ? current.isBefore(positionValidFrom, 'day')
                                : false,
                          }}
                        />
                      </Col>
                    </FormRow>
                    <div className='group-details group-details-with-action'>
                      <strong className='param-title'>Position Parameters</strong>
                      <Button
                        type='button'
                        onClick={() =>
                          setAddingPositionParameters((current) => ({
                            ...current,
                            [positionIndex]: (current[positionIndex] || 0) + 1,
                          }))
                        }
                        icon={<AddIcon />}
                        size={Sizes.Small}
                        variant='outlined'
                        iconPosition={Positions.Right}
                      >
                        Add Row
                      </Button>
                    </div>
                    <InlineParameterList
                      open={open}
                      scopeId={ParameterScope.Position}
                      locationId={Number(watchedPositions[positionIndex]?.locationId) || 0}
                      parameters={positionParameters[positionIndex] || []}
                      isAdding={Boolean(addingPositionParameters[positionIndex])}
                      addingParameterCount={addingPositionParameters[positionIndex] || undefined}
                      onDefinitionsLoaded={(definitions) =>
                        handlePositionDefinitionsLoaded(positionIndex, definitions)
                      }
                      onCancelAdd={(parameterIndex) => {
                        if (parameterIndex !== undefined) {
                          setPositionParameterDrafts((current) =>
                            shiftPositionParameterRecordOnCancel(
                              current,
                              positionIndex,
                              parameterIndex,
                            ),
                          );
                          setPositionParameterErrors((current) =>
                            shiftPositionParameterRecordOnCancel(
                              current,
                              positionIndex,
                              parameterIndex,
                            ),
                          );
                        }
                        setAddingPositionParameters((current) => ({
                          ...current,
                          [positionIndex]: Math.max(0, (current[positionIndex] || 0) - 1),
                        }));
                      }}
                      onRemove={(parameterIndex) =>
                        handlePositionParameterRemove(positionIndex, parameterIndex)
                      }
                      onDraftChange={(parameter, parameterIndex, error) =>
                        handlePositionParameterDraftChange(
                          positionIndex,
                          parameter,
                          parameterIndex,
                          error,
                        )
                      }
                    />
                  </>
                )}
              </div>
            );
          })}
          {positionsError && <ErrorMessage>{positionsError}</ErrorMessage>}
        </div>
      </FormProvider>
    </Modal>
  );
};

export default AddEditContract;
